'use client';
import clsx from 'clsx';
import { SidebarToggleButton } from '@/components/feature-buttons/sidebar-toggle-button';
import { HeaderContainer } from './header-container';
import { HeaderLogo } from './header-logo';
import { HeaderAccount } from './header-account';
import { HeaderEvent } from './header-event';
import { HeaderProduct } from './header-product';
import { HeaderTools } from './header-tools';
import { headerStyle } from './header.style';
import { ViewEnum } from '@/types';
import { useAppearanceStore } from '@/store/appearance';
import { Logo } from '@/components/core/logo';
import { useEffect } from 'react';
import { MessageButton } from '@/components/feature-buttons/message-button';
import { useAccountInfo } from '@/hooks/query/account';
import { useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { BackToMainSite } from '@/features/sidebar/components/back-to-main-site';
import { useSiteStore } from '@/store/site';
import { useStore } from '@/hooks';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { AIChatButton } from '@/features/ai-chat/ai-chat-button';

export const Header = () => {
	const segments = useSelectedLayoutSegments();
	const view = useAppearanceStore(state => state.view);
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const isMarsHome = isAthena? view === ViewEnum.mars && (segments.length < 1 || segments[1] === 'lobby') : segments[1] === 'lobby' && view === ViewEnum.mars;
	const { user } = useAccountInfo();
	const queryClient = useQueryClient();
	const router = useRouter();
	const locale = useLocale();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const setIsShowBalance = useSiteStore(state => state.setIsShowBalance);

	const handleClick = () => {
		const pathName = window.location.pathname;
		const routeList = pathName.split('/');
		if (routeList.length > 2) {
			router.push('/', { locale: locale });
		} else {
			window.location.reload();
		}
	}

	useEffect(() => {
		if (user?.RewardInfo?.EnableRewardSystem) {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.USER_REWADR_INFO],
			})
		}
	}, [user]);

	// 從 HeaderTools 搬過來：這裡不分 view 都會掛載，HeaderTools 只在 mars 才會渲染，不適合放初始化邏輯
	useEffect(() => {
		const storedValue = localStorage.getItem('is-show-balance');
		if (storedValue !== null) {
			setIsShowBalance(storedValue === 'true');
		} else {
			if (siteConfig) {
				setIsShowBalance(siteConfig.IsShowBalance);
			}
		}
	}, [siteConfig]);

	if (view === ViewEnum.galaxy && siteConfig?.EnableHeadlessForGalaxy) return null;

	return (
		<HeaderContainer>
			{isMarsHome ? <MarsHomeHeader /> : null}
			<div
				className={clsx('header__container container', {
					[headerStyle.containerPadding]: view === ViewEnum.mars,
					[headerStyle.marsDesktopHomeHeader]: isMarsHome,
				})}
			>
				<div className="header__left">
					<SidebarToggleButton />
					{
						!isAthena?
							<HeaderLogo />:
							<div onClick={handleClick}>
								<HeaderLogo />
							</div>
					}

					{view === ViewEnum.mars ? <HeaderProduct /> : null}
					<HeaderEvent className="header-event-left" />
				</div>
				<div className="header__right">
					<AIChatButton variant="header" />
					{view === ViewEnum.mars ? <HeaderTools /> : null}
					<HeaderAccount />
				</div>
			</div>
		</HeaderContainer>
	);
};

/** Mars Mobile Home Header */
// MarsHomeHeader 只在 isMarsHome 為真時才會被渲染（該條件已內含 view === ViewEnum.mars），底下的樣式不需要再判斷 view
const MarsHomeHeader = () => {
	const homeUrl = useStore(useSiteStore, state => state.deepLinkSiteInfo.homeUrl);
	return (
		<div className={clsx('header__container container', headerStyle.marsMobileHomeHeader)}>
			<div className="header__left">
				{
					homeUrl
						? <BackToMainSite style={headerStyle} />
						: (
							<div className={headerStyle.homeLogo}>
								<Logo />
							</div>
						)
				}
			</div>
			<div className="header__right">
				<MessageButton
					style={headerStyle.messageButton}
					isOpened={true}
					fromPage="home"
				/>
			</div>
		</div>
	)
}